<script>
import './updates.css'
import MainContainer from '@/components/main/MainContainer.vue'
import UpdateIcon from '@/components/system-updates/UpdateIcon.vue'
import { fetchPublicUpdate } from '@/components/system-updates/publicApi'
import {
	categoryBadgeStyle,
	formatUpdateDate,
	apiErrorMessage,
} from '@/components/system-updates/utils'

// Одно обновление: та же шапка, что у ленты, ниже — полное описание и секции.
// Сюда ведут «Подробнее» в ленте, окно при входе и колокольчик.
export default {
	name: 'SystemUpdatePage',
	components: { MainContainer, UpdateIcon },
	data() {
		return {
			coverUrl: require('@/assets/img/system-updates/page-hero.jpg'),
			item: null,
			loading: true,
			notFound: false,
			error: null,
			previewFailed: false,
			requestSeq: 0,
		}
	},
	computed: {
		id() {
			const id = Number(this.$route.params.id)
			return Number.isInteger(id) && id > 0 ? id : null
		},
		listPath() {
			return '/' + this.$i18n.locale + '/updates'
		},
		hasOwnCover() {
			return Boolean(this.item && this.item.preview_url && !this.previewFailed)
		},
		// Своя картинка обновления (preview_url), иначе — стандартная
		coverSrc() {
			return this.item && this.item.preview_url && !this.previewFailed
				? this.item.preview_url
				: this.coverUrl
		},
		sections() {
			const list = this.item && Array.isArray(this.item.highlights) ? this.item.highlights : []
			return list.filter((h) => h && h.title)
		},
	},
	watch: {
		'$route.params.id'() {
			this.load()
		},
		// Тексты приходят на языке запроса
		'$i18n.locale'() {
			this.load()
		},
	},
	mounted() {
		this.load()
	},
	methods: {
		categoryBadgeStyle,
		formatUpdateDate,

		async load() {
			const seq = ++this.requestSeq
			this.loading = true
			this.notFound = false
			this.error = null
			this.previewFailed = false

			if (!this.id) {
				this.item = null
				this.notFound = true
				this.loading = false
				return
			}

			try {
				const item = await fetchPublicUpdate(this.id)
				if (seq !== this.requestSeq) return
				this.item = item
				this.notFound = !item
				if (item) window.scrollTo(0, 0)
			} catch (error) {
				if (seq !== this.requestSeq) return
				this.item = null
				this.error = apiErrorMessage(error, this.$t('system_updates.load_error'))
			} finally {
				if (seq === this.requestSeq) this.loading = false
			}
		},
	},
	head: {
		title() {
			return {
				inner: (this.item && this.item.title) || this.$t('system_updates.title'),
			}
		},
		meta: function () {
			return [
				{
					name: 'description',
					content: (this.item && this.item.summary) || this.$t('system_updates.hero_text'),
				},
			]
		},
	},
}
</script>

<template>
	<div class="wrap__content sup sup-detail">
		<MainContainer>
			<router-link :to="listPath" class="sup-detail__back">
				<svg
					width="18"
					height="18"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				>
					<path d="M19 12H5M11 18l-6-6 6-6" />
				</svg>
				{{ $t('system_updates.all') }}
			</router-link>

			<div v-if="loading" aria-busy="true">
				<div class="sup-detail__cover sup-skel"></div>
				<div class="sup-detail__header">
					<div class="sup-skel sup-skel--meta"></div>
					<div class="sup-skel sup-skel--title"></div>
				</div>
				<div class="sup-detail__body">
					<div class="sup-skel sup-skel--title"></div>
					<div class="sup-skel sup-skel--line"></div>
					<div class="sup-skel sup-skel--line"></div>
					<div class="sup-skel sup-skel--line sup-skel--short"></div>
				</div>
			</div>

			<div v-else-if="error" class="sup-state">
				<div class="sup-state__title">{{ error }}</div>
				<button type="button" class="sup-btn sup-btn--primary" @click="load">
					{{ $t('system_updates.retry') }}
				</button>
			</div>

			<div v-else-if="notFound || !item" class="sup-state">
				<div class="sup-state__icon">
					<UpdateIcon name="search" :size="28" />
				</div>
				<div class="sup-state__title">{{ $t('system_updates.not_found_one') }}</div>
				<router-link :to="listPath" class="sup-btn sup-btn--ghost">
					{{ $t('system_updates.all') }}
				</router-link>
			</div>

			<template v-else>
				<!-- Картинка — отдельной полосой; название и описание под ней, обычным текстом -->
				<div class="sup-detail__cover" :class="{ 'is-default': !hasOwnCover }">
					<!-- Стандартная картинка: рыба целиком справа, слева — её же размытая копия -->
					<img v-if="!hasOwnCover" class="sup-detail__cover-blur" :src="coverSrc" alt="" />
					<img class="sup-detail__cover-img" :src="coverSrc" alt="" @error="previewFailed = true" />
				</div>

				<header class="sup-detail__header">
					<div class="sup-detail__meta">
						<span v-if="item.is_new" class="sup-badge sup-badge--new">
							{{ $t('system_updates.new') }}
						</span>
						<span
							v-if="item.category && item.category.name"
							class="sup-badge"
							:style="categoryBadgeStyle(item.category)"
						>
							{{ item.category.name }}
						</span>
						<time v-if="item.published_at" class="sup-detail__date" :datetime="item.published_at">
							{{ formatUpdateDate(item.published_at) }}
						</time>
					</div>
					<h1 class="sup-detail__title">{{ item.title }}</h1>
					<p v-if="item.summary" class="sup-detail__summary">{{ item.summary }}</p>
				</header>

				<article class="sup-detail__body">
					<div
						v-if="item.description"
						class="sup-rich sup-detail__description"
						v-html="item.description"
					></div>

					<div v-if="sections.length" class="sup-detail__sections">
						<div
							v-for="(section, index) in sections"
							:key="index"
							class="sup-section sup-detail__section"
							:class="{ 'is-simple': !section.subtitle && !section.description }"
						>
							<span class="sup-section__icon">
								<UpdateIcon :name="section.icon" :size="22" />
							</span>
							<div class="sup-section__body">
								<h2 class="sup-section__title">{{ section.title }}</h2>
								<p v-if="section.subtitle" class="sup-section__subtitle">{{ section.subtitle }}</p>
								<div
									v-if="section.description"
									class="sup-rich sup-detail__section-text"
									v-html="section.description"
								></div>
							</div>
						</div>
					</div>

					<p v-if="!item.description && !sections.length" class="sup-detail__empty">
						{{ item.summary || $t('system_updates.no_details') }}
					</p>
				</article>

				<div class="sup-detail__footer">
					<router-link :to="listPath" class="sup-btn sup-btn--ghost">
						<svg
							width="16"
							height="16"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2.2"
							stroke-linecap="round"
							stroke-linejoin="round"
						>
							<path d="M19 12H5M11 18l-6-6 6-6" />
						</svg>
						{{ $t('system_updates.all') }}
					</router-link>
				</div>
			</template>
		</MainContainer>
	</div>
</template>
